import { generateColors } from "@mantine/colors-generator";

/* eslint-disable no-magic-numbers */

/**
 * Converts a hex color to HSL values.
 * @param hex
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  const [, rHex, gHex, bHex] = result;
  if (!rHex || !gHex || !bHex) return { h: 0, s: 0, l: 0 };

  const r = Number.parseInt(rHex, 16) / 255;
  const g = Number.parseInt(gHex, 16) / 255;
  const b = Number.parseInt(bHex, 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = getHueFromRgb(max, r, g, b, d);
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  return { h: 0, s: 0, l: l * 100 };
}

/**
 * Calculates hue from normalized RGB values.
 * @param max
 * @param r
 * @param g
 * @param b
 * @param d
 */
function getHueFromRgb(max: number, r: number, g: number, b: number, d: number): number {
  if (max === r) return ((g - b) / d + (g < b ? 6 : 0)) / 6;
  if (max === g) return ((b - r) / d + 2) / 6;
  return ((r - g) / d + 4) / 6;
}

/**
 * Converts HSL values to a hex color.
 * @param h
 * @param s
 * @param l
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  const { r, g, b } = getRgbFromHue(h, c, x);

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/* eslint-disable complexity */
/**
 * Calculates RGB components based on hue sector.
 * @param h
 * @param c
 * @param x
 */
function getRgbFromHue(h: number, c: number, x: number): { r: number; g: number; b: number } {
  if (h >= 0 && h < 60) return { r: c, g: x, b: 0 };
  if (h >= 60 && h < 120) return { r: x, g: c, b: 0 };
  if (h >= 120 && h < 180) return { r: 0, g: c, b: x };
  if (h >= 180 && h < 240) return { r: 0, g: x, b: c };
  if (h >= 240 && h < 300) return { r: x, g: 0, b: c };
  if (h >= 300 && h < 360) return { r: c, g: 0, b: x };
  return { r: 0, g: 0, b: 0 };
}

/**
 * Generate a full color palette from a single primary color.
 * @param primaryHex
 */
export function generatePaletteFromPrimary(primaryHex: string): {
  primary: string;
  primary1: string;
  primary6: string;
  primary7: string;
  primary8: string;
  secondary: string;
  secondary4: string;
  accent: string;
  primaryAlt: string;
  success: string;
  successAlt: string;
  warning: string;
  danger: string;
} {
  const hsl = hexToHsl(primaryHex);

  // Generate primary palette shades using Mantine
  const primaryPalette = generateColors(primaryHex);

  // Complementary color (opposite on color wheel)
  const complementaryHue = (hsl.h + 180) % 360;
  const secondary = hslToHex(complementaryHue, Math.min(hsl.s, 70), 45);
  const secondaryPalette = generateColors(secondary);

  // Analogous color for accent (30 degrees away)
  const analogousHue = (hsl.h + 30) % 360;
  const accent = hslToHex(analogousHue, Math.min(hsl.s + 10, 85), 50);

  // Triadic color for primaryAlt (120 degrees away)
  const triadicHue = (hsl.h + 120) % 360;
  const primaryAlt = hslToHex(triadicHue, Math.min(hsl.s, 75), 50);

  // Semantic colors with consistent saturation/lightness
  const success = "#2F9E44";
  const successAlt = "#00FF73";
  const warning = "#F08C00";
  const danger = "#E03131";

  return {
    primary: primaryHex,
    primary1: primaryPalette[1],
    primary6: primaryPalette[6],
    primary7: primaryPalette[7],
    primary8: primaryPalette[8],
    secondary,
    secondary4: secondaryPalette[4],
    accent,
    primaryAlt,
    success,
    successAlt,
    warning,
    danger
  };
}
